import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";

export const PAGE_SIZES = [10, 25, 50, 100];

/** Debounce a rapidly changing value (search inputs) before it hits the server. */
export const useDebounced = (value, ms = 350) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

/** Skeleton for card-style record lists (grievances, applications, approvals). */
export const CardListSkeleton = ({ rows = 6, testId = "list-skeleton" }) => (
  <div className="space-y-3" aria-hidden="true" data-testid={testId}>
    {Array.from({ length: rows }).map((_, i) => (
      <Card key={i} className="shadow-sm"><CardContent className="p-4 flex items-center justify-between gap-3">
        <div className="space-y-2 flex-1"><Skeleton className="h-4 w-56" /><Skeleton className="h-4 w-80 max-w-full" /><Skeleton className="h-3 w-64 max-w-full" /></div>
        <Skeleton className="h-8 w-20" />
      </CardContent></Card>
    ))}
  </div>
);

/** Skeleton rows for table-style lists. */
export const TableRowsSkeleton = ({ rows = 6, cols = 5, testId = "table-skeleton" }) => (
  <div className="divide-y" aria-hidden="true" data-testid={testId}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="grid gap-4 px-4 py-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {Array.from({ length: cols }).map((__, j) => <Skeleton key={j} className="h-4" />)}
      </div>
    ))}
  </div>
);

/**
 * PaginationBar — "Showing X–Y of Z" + Previous / Next + page-size picker.
 * Purely presentational; works for server-side and client-side paging alike.
 */
export const PaginationBar = ({ page, limit, total, onPageChange, onLimitChange, loading = false,
  testId = "pagination", className = "", sizes = PAGE_SIZES }) => {
  const pages = Math.max(1, Math.ceil((total || 0) / (limit || 1)));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total || 0);
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 pt-3 ${className}`} data-testid={testId}>
      <p className="text-xs text-muted-foreground tabular-nums" data-testid={`${testId}-label`}>
        {total === 0 ? "No records" : <>Showing <span className="font-medium text-foreground">{from}–{to}</span> of {total.toLocaleString("en-IN")}</>}
      </p>
      <div className="flex items-center gap-2">
        {onLimitChange && (
          <Select value={String(limit)} onValueChange={(v) => onLimitChange(Number(v))}>
            <SelectTrigger className="h-8 w-[112px] text-xs" data-testid={`${testId}-size`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sizes.map((s) => <SelectItem key={s} value={String(s)} className="text-xs">{s} per page</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button variant="outline" size="sm" className="h-8" disabled={page <= 1 || loading}
          onClick={() => onPageChange(page - 1)} data-testid={`${testId}-prev`} aria-label="Previous page">
          <ChevronLeft className="h-4 w-4" /> <span className="hidden sm:inline">Previous</span>
        </Button>
        <span className="text-xs text-muted-foreground tabular-nums px-1" data-testid={`${testId}-page`}>
          Page {page} / {pages}
        </span>
        <Button variant="outline" size="sm" className="h-8" disabled={page >= pages || loading}
          onClick={() => onPageChange(page + 1)} data-testid={`${testId}-next`} aria-label="Next page">
          <span className="hidden sm:inline">Next</span> <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

/**
 * usePagedList — server-side pagination against Phase 83 list endpoints that return
 * {items,total,page,limit,pages} when `page` is supplied. Keeps the previous page on
 * screen while the next one loads (no flash), tracks the load error, and exposes
 * `reload()` for post-action refreshes.
 *
 * Phase 133 (Fetch gating): `enabled=false` means "nothing requested yet" — no request is made and
 * `loading` stays false so the page can show a Fetch prompt instead of a skeleton. `reloadKey` is any
 * value that, when changed, re-runs the query even if `params` did not change (the Fetch button's
 * version counter). Page / limit changes always fetch immediately.
 */
export const usePagedList = (path, { params = {}, initialLimit = 25, enabled = true, reloadKey = 0 } = {}) => {
  const [page, setPage] = useState(1);
  const [limit, setLimitState] = useState(initialLimit);
  const [data, setData] = useState(null); // {items,total,page,limit,pages}
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState(null);
  const paramsKey = JSON.stringify(params);
  const reqId = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) return;
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(path, { params: { ...JSON.parse(paramsKey), page, limit } });
      if (id !== reqId.current) return; // stale response
      const d = r.data;
      setData(Array.isArray(d) ? { items: d, total: d.length, page: 1, limit: d.length || limit, pages: 1 } : d);
    } catch (e) {
      if (id === reqId.current) setError(e);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [path, paramsKey, page, limit, enabled, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // filter/search changes go back to the first page
  useEffect(() => { setPage(1); }, [paramsKey]);

  const setLimit = (n) => { setLimitState(n); setPage(1); };
  return { page, setPage, limit, setLimit, data, items: data?.items || [], total: data?.total || 0,
    loading, error, reload: load, enabled: !!enabled };
};

/** Client-side helper: slice an already-loaded array for the current page. */
export const usePageSlice = (items, initialLimit = 20) => {
  const [page, setPageRaw] = useState(1);
  const [limit, setLimitRaw] = useState(initialLimit);
  const total = items?.length || 0;
  const pages = Math.max(1, Math.ceil(total / limit));
  useEffect(() => { if (page > pages) setPageRaw(pages); }, [page, pages]);
  const cur = Math.min(page, pages);
  const slice = (items || []).slice((cur - 1) * limit, cur * limit);
  const setPage = useCallback((p) => setPageRaw((c) => Math.max(1, typeof p === "function" ? p(c) : p)), []);
  const setLimit = useCallback((n) => { setLimitRaw(n); setPageRaw(1); }, []);
  const resetPage = useCallback(() => setPageRaw(1), []);
  return { page: cur, setPage, limit, setLimit, total, slice, resetPage };
};
